import { VideoPlayer } from '@livecontrol/core-ui';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCcConfig } from '../../store';
import { useInViewport } from '../use-in-viewport';
import { useStaggeredMount } from '../use-staggered-mount';
import type { QuickSwitchEvent } from './event';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
  text-align: left;
  padding: 6px;
  border-radius: 11px;
  border: 1px solid transparent;
  background: transparent;
  color: inherit;
  text-decoration: none;
  transition: all var(--transition);

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const Thumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background: #0b1226;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    color: var(--white35);
    font-size: 18px;
  }

  .video {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  /* Fill the 16:9 thumb, cropping the sides instead of letterboxing the stream. */
  .video video {
    object-fit: cover !important;
  }
`;

const LiveBadge = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 16, 45, 0.72);
  border-radius: 5px;
  padding: 3px 6px;
  letter-spacing: 0.06em;
  color: rgb(255, 255, 255);
  font-size: 8px;
  font-weight: 700;
  z-index: 2;

  .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgb(53, 211, 153);
  }
`;

const PriorityDot = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(255, 80, 95);
  border: 2px solid rgba(0, 16, 45, 0.8);
  z-index: 2;
`;

const Meta = styled.div`
  width: 100%;
  min-width: 0;
  padding: 0 2px 2px;

  .primary {
    color: rgba(213, 221, 238, 0.9);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
    font-size: 12px;
  }

  .secondary {
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.55);
  }
`;

interface Props {
  event: QuickSwitchEvent;
}

export const QuickSwitchCard = ({ event }: Props): ReactElement => {
  const autoplaySidebar = useCcConfig((v) => v.autoplaySidebar);

  /*
   * The Quick Switch list scrolls inside the rail and the sidebar is mounted on
   * every screen, so its players would otherwise keep running app-wide. Mount
   * one only while the card is actually visible; a smaller margin than the event
   * list because this list is short and scrolls fast.
   */
  const [thumbRef, thumbVisible] = useInViewport<HTMLDivElement>('100px');

  const wantsVideo = event.isLive && autoplaySidebar && thumbVisible;

  /*
   * The same queue the event grid uses, and that is the point of it being shared: the sidebar is
   * mounted on every screen, so without this its players would build in parallel with the grid's
   * during a batch of events going live — the one moment the staggering exists for. With no
   * contention a slot is granted immediately, so on a quiet screen nothing waits.
   */
  const streamUrl = useStaggeredMount(wantsVideo) ? event.muxStreamUrl : undefined;

  return (
    <Card to={event.to}>
      <Thumb ref={thumbRef}>
        {streamUrl !== undefined ? (
          <div className='video'>
            <VideoPlayer
              key={streamUrl}
              options={{
                autoplay: true,
                muted: true,
                controls: false,
                fill: true,
                aspectRatio: '16:9',
                preload: 'metadata',
                poster: event.thumbnail,
                sources: [{ src: streamUrl, type: 'application/x-mpegURL' }]
              }}
            />
          </div>
        ) : event.thumbnail !== undefined ? (
          <img src={event.thumbnail} alt={event.title} />
        ) : (
          <div className='placeholder'>
            <i className='far fa-image' />
          </div>
        )}
        {event.isLive && (
          <LiveBadge>
            <span className='dot cc-blink-slow' />
            Live
          </LiveBadge>
        )}
        {event.highPriority && <PriorityDot />}
      </Thumb>

      <Meta>
        <div className='primary'>{event.organizationName ?? event.title}</div>
        {event.organizationName !== undefined && <div className='secondary'>{event.title}</div>}
      </Meta>
    </Card>
  );
};
